import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import apiFetch from '../../services/apiFetch';
import './CourseDurationModal.css';

export function changedTopics(topics) {
    return topics.filter(t => t.old_duration !== t.new_duration);
}

export function projectedCourseTotal(topics, selectedIds) {
    return topics.reduce((sum, t) => (
        sum + (selectedIds.has(t.topic_id) ? t.new_duration : (t.old_duration || 0))
    ), 0);
}

export default function CourseDurationModal({ visible, course, token, onClose }) {
    // loading | review | up-to-date | applying | done | error
    const [phase, setPhase] = useState('loading');
    const [topics, setTopics] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [result, setResult] = useState(null); // apply response
    const [errorMsg, setErrorMsg] = useState('');

    const loadPreview = useCallback(async () => {
        if (!course?.id) return;
        setErrorMsg('');
        setPhase('loading');
        try {
            const res = await apiFetch(
                `/api/courses/${course.id}/estimate-durations`,
                token,
                { method: 'POST', body: JSON.stringify({ dry_run: true }) },
            );
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to estimate durations (${res.status})`);
            }
            const data = await res.json();
            const previewTopics = data.topics || [];
            const changed = changedTopics(previewTopics);
            setTopics(previewTopics);
            setSelected(new Set(changed.map(t => t.topic_id)));
            setPhase(changed.length === 0 ? 'up-to-date' : 'review');
        } catch (err) {
            setErrorMsg(err.message || 'Failed to estimate durations.');
            setPhase('error');
        }
    }, [course?.id, token]);

    // Fetch a fresh preview every time the modal opens
    useEffect(() => {
        if (visible) {
            setResult(null);
            loadPreview();
        }
    }, [visible, loadPreview]);

    const applySelected = useCallback(async () => {
        if (!course?.id || selected.size === 0) return;
        setErrorMsg('');
        setPhase('applying');
        try {
            const res = await apiFetch(
                `/api/courses/${course.id}/estimate-durations`,
                token,
                { method: 'POST', body: JSON.stringify({ topic_ids: [...selected] }) },
            );
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to apply durations (${res.status})`);
            }
            const data = await res.json();
            setResult(data);
            setPhase('done');
        } catch (err) {
            setErrorMsg(err.message || 'Failed to apply durations.');
            setPhase('error');
        }
    }, [course?.id, token, selected]);

    const handleClose = useCallback(() => {
        onClose({
            courseId: course?.id,
            updated: phase === 'done',
            courseDuration: result?.course_duration ?? null,
        });
    }, [onClose, course?.id, phase, result]);

    const toggleTopic = (topicId) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(topicId)) next.delete(topicId);
            else next.add(topicId);
            return next;
        });
    };

    const changed = changedTopics(topics);

    const toggleAll = () => {
        setSelected(prev => (
            prev.size === changed.length ? new Set() : new Set(changed.map(t => t.topic_id))
        ));
    };

    const renderLoading = (label) => (
        <div className="cdm-center">
            <div className="cdm-spinner" />
            <p className="cdm-desc" style={{ marginTop: 12 }}>{label}</p>
        </div>
    );

    const renderUpToDate = () => (
        <div className="cdm-body">
            <div className="cdm-done-summary">
                <i className="pi pi-check-circle cdm-done-icon" />
                <div>
                    <div className="cdm-done-title">All up to date</div>
                    <div className="cdm-done-sub">
                        {topics.length === 0
                            ? 'No topics in this course.'
                            : 'Every topic duration already matches the estimate.'}
                    </div>
                </div>
            </div>
            {topics.length > 0 && (
                <div className="cdm-topic-list">
                    {topics.map(t => (
                        <div key={t.topic_id} className="cdm-topic-row">
                            <span className="cdm-topic-name cdm-row-muted">{t.name}</span>
                            <span className="cdm-topic-change">
                                <span className="cdm-unchanged">{t.new_duration} min</span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <div className="cdm-actions">
                <button className="cdm-btn cdm-btn-primary" onClick={handleClose}>Close</button>
            </div>
        </div>
    );

    const renderReview = () => {
        const currentTotal = topics.reduce((sum, t) => sum + (t.old_duration || 0), 0);
        const projectedTotal = projectedCourseTotal(topics, selected);
        return (
            <div className="cdm-body">
                <p className="cdm-desc" style={{ marginBottom: 12, maxWidth: 'none' }}>
                    {changed.length} of {topics.length} {topics.length === 1 ? 'topic' : 'topics'} differ
                    from the estimated reading time. Pick which to update.
                </p>
                <div className="cdm-topic-list">
                    <div className="cdm-topic-row cdm-list-header">
                        <label className="cdm-check-label">
                            <input
                                type="checkbox"
                                checked={selected.size === changed.length}
                                onChange={toggleAll}
                            />
                            <span>Topic</span>
                        </label>
                        <span>Duration</span>
                    </div>
                    {topics.map(t => {
                        const isChanged = t.old_duration !== t.new_duration;
                        return (
                            <div key={t.topic_id} className="cdm-topic-row">
                                {isChanged ? (
                                    <label className="cdm-check-label cdm-topic-name">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(t.topic_id)}
                                            onChange={() => toggleTopic(t.topic_id)}
                                        />
                                        <span>{t.name}</span>
                                    </label>
                                ) : (
                                    <span className="cdm-topic-name cdm-row-muted">{t.name}</span>
                                )}
                                <span className="cdm-topic-change">
                                    {isChanged ? (
                                        <>
                                            <span className="cdm-old">{t.old_duration ?? '—'} min</span>
                                            {' → '}
                                            <strong>{t.new_duration} min</strong>
                                        </>
                                    ) : (
                                        <span className="cdm-unchanged">{t.new_duration} min</span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
                <div className="cdm-total">
                    Course total:{' '}
                    {projectedTotal === currentTotal ? (
                        <strong>{currentTotal} min</strong>
                    ) : (
                        <>
                            <span className="cdm-old">{currentTotal} min</span>
                            {' → '}
                            <strong>{projectedTotal} min</strong>
                        </>
                    )}
                </div>
                <div className="cdm-actions">
                    <button className="cdm-btn" onClick={handleClose}>Cancel</button>
                    <button
                        className="cdm-btn cdm-btn-primary"
                        disabled={selected.size === 0}
                        onClick={applySelected}
                    >
                        <i className="pi pi-check" />
                        Apply {selected.size} {selected.size === 1 ? 'change' : 'changes'}
                    </button>
                </div>
            </div>
        );
    };

    const renderDone = () => {
        const resultTopics = result?.topics || [];
        const applied = resultTopics.filter(t => t.applied);
        const skipped = changed.filter(t => !selected.has(t.topic_id));
        return (
            <div className="cdm-body">
                <div className="cdm-done-summary">
                    <i className="pi pi-check-circle cdm-done-icon" />
                    <div>
                        <div className="cdm-done-title">Durations updated</div>
                        <div className="cdm-done-sub">
                            {`Applied ${applied.length} of ${changed.length} ${changed.length === 1 ? 'change' : 'changes'} · course total ${result.course_duration} min`}
                        </div>
                    </div>
                </div>
                <div className="cdm-topic-list">
                    {applied.map(t => (
                        <div key={t.topic_id} className="cdm-topic-row">
                            <span className="cdm-topic-name">{t.name}</span>
                            <span className="cdm-topic-change">
                                <span className="cdm-old">{t.old_duration ?? '—'} min</span>
                                {' → '}
                                <strong>{t.new_duration} min</strong>
                            </span>
                        </div>
                    ))}
                    {skipped.map(t => (
                        <div key={t.topic_id} className="cdm-topic-row">
                            <span className="cdm-topic-name cdm-row-muted">{t.name}</span>
                            <span className="cdm-topic-change">
                                <span className="cdm-unchanged">kept {t.old_duration ?? '—'} min</span>
                            </span>
                        </div>
                    ))}
                </div>
                <div className="cdm-actions">
                    <button className="cdm-btn cdm-btn-primary" onClick={handleClose}>Close</button>
                </div>
            </div>
        );
    };

    const renderError = () => (
        <div className="cdm-center">
            <div className="cdm-icon cdm-icon-error"><i className="pi pi-exclamation-circle" /></div>
            <h3 className="cdm-title">Something went wrong</h3>
            <p className="cdm-desc">{errorMsg}</p>
            <div className="cdm-actions">
                <button className="cdm-btn" onClick={handleClose}>Close</button>
                <button className="cdm-btn cdm-btn-primary" onClick={loadPreview}>
                    <i className="pi pi-refresh" /> Try again
                </button>
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={course?.name ? `Reading time: ${course.name}` : 'Reading time'}
            modal
            style={{ width: '520px', maxWidth: '95vw' }}
            onHide={handleClose}
            className="course-duration-dialog"
        >
            {phase === 'loading' && renderLoading('Estimating reading time…')}
            {phase === 'applying' && renderLoading('Applying changes…')}
            {phase === 'up-to-date' && renderUpToDate()}
            {phase === 'review' && renderReview()}
            {phase === 'done' && renderDone()}
            {phase === 'error' && renderError()}
        </Dialog>
    );
}

CourseDurationModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    course: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
    }),
    token: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

CourseDurationModal.defaultProps = {
    course: null,
};
